// ONE source of truth for every time X axis (recharts, visx, the table
// strips): which ticks to draw and how to label them. Rule (Alexey 26.10
// #2646 «многие оси х всё ещё не пишут даты»): when the visible range covers
// more than one calendar day (MSK) the axis must tie every stretch to a date —
// midnights read «пн 20.10», the first tick of each day «20.10 14:00»; a
// single-day view stays plain «14:00». Zooming into a window across midnight
// counts as multi-day too.
//
// Everything here is pinned to Moscow time (UTC+3, no DST since 2014): the
// business runs in MSK whatever the viewer's browser tz (same rule as
// hover-time.tsx useFrameTime), and fixed-offset arithmetic keeps tick
// alignment and labels in agreement.

const MSK_OFFSET_MS = 3 * 3600 * 1000;
const DAY_MS = 24 * 3600 * 1000;
const MIN_MS = 60_000;

/** Wall-clock parts of `ms` in MSK (read with getUTC*). */
const msk = (ms: number) => new Date(ms + MSK_OFFSET_MS);
/** Calendar-day number of `ms` in MSK (stable key for «same day?»). */
export const mskDayKey = (ms: number) => Math.floor((ms + MSK_OFFSET_MS) / DAY_MS);
/** MSK midnight at or before `ms`. */
export const mskMidnight = (ms: number) => mskDayKey(ms) * DAY_MS - MSK_OFFSET_MS;
const isMskMidnight = (ms: number) => (ms + MSK_OFFSET_MS) % DAY_MS === 0;

/** True when [d0, d1] covers more than one MSK calendar day. The old
 *  «span > 2 days» test left 1–2-day windows (and a zoom across midnight)
 *  with bare HH:MM ticks — «легко запутаться» (client via Alexey 13.10, #5799).
 *  `d1` is exclusive-ish: a domain ending exactly at midnight stays one day. */
export function spansDays(d0: number, d1: number): boolean {
  if (!Number.isFinite(d0) || !Number.isFinite(d1)) return false;
  return mskDayKey(d0) !== mskDayKey(Math.max(d0, d1 - 1));
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/** «14:30» (MSK) */
export function fmtClockMsk(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const d = msk(ms);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** «20.10» (MSK) */
function fmtDateMsk(ms: number): string {
  const d = msk(ms);
  return `${pad2(d.getUTCDate())}.${pad2(d.getUTCMonth() + 1)}`;
}

/** «пн 13.10» */
export function fmtDayShort(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  return `${WEEKDAYS[msk(ms).getUTCDay()]} ${fmtDateMsk(ms)}`;
}

/** «пн 13.10, 14:30» — tooltips always carry the date, whatever the range. */
export function fmtDayTime(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  return `${fmtDayShort(ms)}, ${fmtClockMsk(ms)}`;
}

/** Tick positions for a time axis over [d0, d1], ~`target` of them. Always
 *  labels the domain start (recharts' auto ticks began somewhere INSIDE the
 *  domain, so the working-day open read as a cropped chart), then continues
 *  on round MSK steps — which, on a multi-day domain, land on every midnight
 *  (or every N-th day for long periods), so the date labels have a home.
 *  Few ticks on long ranges: a week is 7 day ticks, not 14 day+noon. */
export function timeAxisTicks(d0: number, d1: number, target = 8): number[] {
  if (!Number.isFinite(d0) || !Number.isFinite(d1)) return [];
  const span = d1 - d0;
  if (!(span > 0)) return [d0];
  // Intra-day steps (minutes) all divide 24 h, so aligning them to MSK
  // midnight keeps midnight itself a tick; day steps count from midnight.
  // 1–2 мин — для приближения коротких участков (стол «грязный» 3 мин ±10 %: ось оставалась без
  // подписей, Алексей 28.10 #3076).
  const stepsMin = [1, 2, 5, 10, 15, 30, 60, 120, 180, 240, 360, 720, 1440, 2 * 1440, 3 * 1440, 7 * 1440, 14 * 1440, 28 * 1440];
  const step = (stepsMin.find((m) => span / (m * MIN_MS) <= Math.max(1, target)) ?? stepsMin[stepsMin.length - 1]) * MIN_MS;
  let ticks = [d0];
  const mid = mskMidnight(d0);
  let t = step <= DAY_MS ? mid + Math.ceil((d0 - mid) / step) * step : mid + (mid < d0 ? DAY_MS : 0);
  if (t === d0) t += step;
  else if (t - d0 < step * 0.45) {
    // Too close to the start label. A midnight wins over a start just
    // before it (23:50 → «вт 21.10»); any other round tick is skipped.
    if (isMskMidnight(t) && t - d0 < step * 0.25) ticks = [];
    else if (!isMskMidnight(t)) t += step;
  }
  for (; t <= d1 - Math.min(step * 0.35, DAY_MS / 4); t += step) ticks.push(t);
  return ticks.length ? ticks : [d0];
}

/** X tick label on a time axis. One day → clock. Multi-day domain → MSK
 *  midnights read «пн 13.10», the first tick of every other day (incl. the
 *  domain start) «13.10 22:00», the rest the clock. Pass the axis' `ticks`
 *  (from timeAxisTicks) so «first tick of the day» is exact; without them
 *  only the domain start gets the date prefix. */
export function fmtTimeTick(ms: number, domain?: [number, number], ticks?: number[]): string {
  if (!Number.isFinite(ms)) return '';
  const clock = fmtClockMsk(ms);
  if (!domain || !spansDays(domain[0], domain[1])) return clock;
  if (isMskMidnight(ms)) return fmtDayShort(ms);
  let firstOfDay: boolean;
  if (ticks && ticks.length) {
    let prev: number | undefined;
    for (const tk of ticks) if (tk < ms && (prev === undefined || tk > prev)) prev = tk;
    firstOfDay = prev === undefined || mskDayKey(prev) !== mskDayKey(ms);
  } else {
    firstOfDay = Math.abs(ms - domain[0]) < MIN_MS;
  }
  return firstOfDay ? `${fmtDateMsk(ms)} ${clock}` : clock;
}

/** Both halves at once: `<XAxis {...timeAxisProps([d0, d1])} />`-style use.
 *  `format` already closes over the ticks, so labels and ticks can't drift. */
export function makeTimeAxis(domain: [number, number] | undefined, target = 8): {
  ticks: number[] | undefined;
  format: (v: unknown) => string;
} {
  if (!domain || !Number.isFinite(domain[0]) || !Number.isFinite(domain[1])) {
    return { ticks: undefined, format: (v) => fmtClockMsk(Number(v instanceof Date ? v.getTime() : v)) };
  }
  const ticks = timeAxisTicks(domain[0], domain[1], target);
  return { ticks, format: (v) => fmtTimeTick(Number(v instanceof Date ? v.getTime() : v), domain, ticks) };
}

/** Labels for a CATEGORY axis of ISO time buckets (recharts picks which
 *  categories survive, so «first of the day» can't be known): multi-day →
 *  every intraday label carries «dd.mm HH:MM» (midnights «пн 13.10»);
 *  one day → «HH:MM». Date-only buckets («2026-10-20») → «20.10». */
export function fmtBucketTick(iso: string, multiDay: boolean): string {
  if (!iso) return '';
  if (iso.indexOf('T') < 0) {
    const parts = iso.split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}` : iso;
  }
  const ms = parseBucketMs(iso);
  if (Number.isNaN(ms)) return iso;
  if (!multiDay) return fmtClockMsk(ms);
  return isMskMidnight(ms) ? fmtDayShort(ms) : `${fmtDateMsk(ms)} ${fmtClockMsk(ms)}`;
}

/** Do these ISO buckets cover more than one MSK day? (category axes) */
export function bucketsSpanDays(isos: readonly string[]): boolean {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of isos) {
    if (!s || s.indexOf('T') < 0) continue;
    const ms = parseBucketMs(s);
    if (Number.isNaN(ms)) continue;
    if (ms < lo) lo = ms;
    if (ms > hi) hi = ms;
  }
  return Number.isFinite(lo) && mskDayKey(lo) !== mskDayKey(hi);
}

/** Bucket label from the BE → epoch ms. Hour buckets come as «2026-10-08T05:00»
 *  WITHOUT an offset, and that hour is UTC (`_fmt_bucket` strftime of an aware UTC
 *  TruncHour). `new Date()` read it as LOCAL time, so a week of «Выкладка» drew
 *  every point 3 h early: the 08:16 photo sat mid-chart and the day «started» at
 *  05:00 (Alexey 13.10 #5811). Offset-less → UTC; anything with an offset as is. */
export function parseBucketMs(d: string): number {
  return new Date(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(d) ? `${d}Z` : d).getTime();
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** Human period for the header: «1 окт 08:00 – 8 окт 12:37», same day —
 *  «7 окт 08:00 – 12:37» (Alexey 13.10 #5805: «человекочитаемо, а не че попало»). */
export function fmtPeriod(d0: number, d1: number): string {
  if (!Number.isFinite(d0) || !Number.isFinite(d1)) return '';
  const day = (ms: number) => `${msk(ms).getUTCDate()} ${MONTHS[msk(ms).getUTCMonth()]}`;
  return spansDays(d0, d1 + 1)
    ? `${day(d0)} ${fmtClockMsk(d0)} – ${day(d1)} ${fmtClockMsk(d1)}`
    : `${day(d0)} ${fmtClockMsk(d0)} – ${fmtClockMsk(d1)}`;
}
